import { ApiClient } from '@api/api-configuration';
import { Doctor } from '@api/READ_ONLY/doctors_api/Api';
import AppAvatar from '@app/components/ui/Avatar';
import {
  GenericSearchSelect,
  type ApiFetchFunction,
  type ApiQueryParams,
} from '@app/components/ui/GenericSearchSelect';
import { getInitials } from '@app/utils/extractFullnameInitials';
import { SelectProps } from 'antd';
import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The doctor picker, shaped like the caregiver one.
 *
 * It replaced a plain Select that fetched 500 doctors at mount and filtered them in
 * the browser. That works while a deployment has a handful of doctors and stops
 * working silently at the 501st — the missing one just never appears in the list,
 * with nothing to say the roster was truncated. `GET /doctors` already takes the
 * same `query`/`offset`/`limit` the caregiver roster does, so the search belongs
 * on the server for the same reasons it does there.
 */
type DoctorsSelectProps = {
  onDoctorChange?: (doctor: Doctor | Doctor[] | undefined) => void;
  placeholder?: string;
  pageSize?: number;
  minSearchLength?: number;
  searchDebounceMs?: number;
  reloadSignal?: number | string;
  lazyLoadOnOpen?: boolean;
  selectedItemsProvider?: () => Doctor | Doctor[] | undefined;
  /** Called when a page could not be fetched, so a caller can say so on the field. */
  onFetchError?: (failed: boolean) => void;
} & Omit<SelectProps, 'options' | 'onChange' | 'loading' | 'onSearch'>;

const DoctorOption = memo(({ firstName, lastName }: { firstName: string; lastName: string }) => {
  const fullname = `${firstName} ${lastName}`;
  const fullnameLetters = getInitials(fullname, { fallback: '' });

  return (
    <div className="flex items-center gap-2 min-w-0 w-full">
      <span className="flex-none">
        <AppAvatar isSelected={true} fullName={fullname} value={fullnameLetters} />
      </span>
      <span className="flex-1 min-w-0 truncate">{`${firstName}, ${lastName}`}</span>
    </div>
  );
});
DoctorOption.displayName = 'DoctorOption';

export const DoctorsSelectGeneric = ({
  onDoctorChange,
  placeholder,
  pageSize = 10,
  minSearchLength = 3,
  searchDebounceMs = 300,
  reloadSignal,
  lazyLoadOnOpen,
  selectedItemsProvider,
  onFetchError,
  ...selectProps
}: DoctorsSelectProps) => {
  const { t } = useTranslation();

  /**
   * The failure is reported AND rethrown. GenericSearchSelect logs it to Sentry and
   * leaves the dropdown empty, which on a required field reads as "this deployment
   * has no doctors" — so the caller gets told too, and can say so where the
   * operator is looking.
   */
  const fetchDoctors = useMemo<ApiFetchFunction<Doctor>>(
    () => async (params: ApiQueryParams) => {
      try {
        const response = await ApiClient.doctorsApi.doctors.listDoctors(params);
        onFetchError?.(false);
        return response;
      } catch (error) {
        onFetchError?.(true);
        throw error;
      }
    },
    [onFetchError]
  );

  return (
    <GenericSearchSelect<Doctor>
      {...selectProps}
      apiFetch={fetchDoctors}
      getItemId={(doctor) => doctor.doctorId}
      getItemLabel={(doctor) => `${doctor.firstName} ${doctor.lastName}`}
      onSelectionChange={onDoctorChange}
      placeholder={placeholder ?? t('doctors-select-placeholder')}
      pageSize={pageSize}
      minSearchLength={minSearchLength}
      searchDebounceMs={searchDebounceMs}
      searchPlaceholder={t('doctors-search-select-placeholder')}
      reloadSignal={reloadSignal}
      lazyLoadOnOpen={lazyLoadOnOpen}
      selectedItemsProvider={selectedItemsProvider}
      renderOption={(doctor) => <DoctorOption firstName={doctor.firstName} lastName={doctor.lastName} />}
    />
  );
};

export default DoctorsSelectGeneric;
